/** shadcn:input — upstream synced via `pnpm dlx shadcn add input --diff` */

import * as React from 'react';

import { cn } from '@/lib/utils';

// @genesys-start — editor input behaviour (prefix/suffix, focus guard, Escape blur)
export interface InputProps extends React.ComponentProps<'input'> {
  /** Select all text when the input receives focus */
  selectOnFocus?: boolean;
  /** Prefix label or content to display inside the input on the left */
  prefixLabel?: React.ReactNode;
  /** Suffix label or content to display inside the input on the right */
  suffixLabel?: React.ReactNode;
  /** Custom padding class when prefix is present (default: 'pl-6') */
  prefixPadding?: string;
  /** Custom padding class when suffix is present (default: 'pr-1') */
  suffixPadding?: string;
}

/**
 * Input component with optional prefix and suffix labels
 * @example
 * // Basic usage
 * <Input placeholder="Enter value" />
 *
 * // With prefix label
 * <Input prefixLabel="$" placeholder="0.00" />
 *
 * // With suffix label
 * <Input suffixLabel="USD" placeholder="Amount" />
 *
 * // With both prefix and suffix
 * <Input prefixLabel="X:" suffixLabel="px" placeholder="0" />
 */
function Input({
  className,
  type,
  selectOnFocus = true,
  prefixLabel,
  suffixLabel,
  prefixPadding = 'pl-6',
  suffixPadding = 'pr-1',
  onKeyDown,
  onClick,
  onFocus,
  onPointerDown,
  ...props
}: InputProps) {
  const wasFocusedOnPointerDownRef = React.useRef(false);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.currentTarget.blur();
    }
    onKeyDown?.(e);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLInputElement>) => {
    wasFocusedOnPointerDownRef.current = document.activeElement === e.currentTarget;
    onPointerDown?.(e);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    onFocus?.(e);

    if (selectOnFocus && !wasFocusedOnPointerDownRef.current) {
      e.currentTarget.select();
    }
  };

  const inputElement = (
    <input
      type={type}
      data-slot="input"
      className={cn(
        // Layout → size → spacing → shape → border colour
        'flex h-7 w-full min-w-0 rounded-md border-input px-3 py-1',
        // Typography, caret, surface, effects, motion
        'bg-transparent text-xs font-medium text-secondary-foreground caret-primary shadow-xs outline-none transition-[color,box-shadow,background-color]',
        // Pseudo-element colours (placeholder, selection, file)
        'placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground',
        // Dark mode surface
        'dark:bg-input/10 dark:hover:bg-input/20',
        // Interactive states
        'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
        'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-primary',
        'aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40',
        // Adjust padding when prefix/suffix are present
        prefixLabel && prefixPadding,
        suffixLabel && suffixPadding,
        className
      )}
      onClick={onClick}
      onPointerDown={handlePointerDown}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      {...props}
    />
  );

  if (!prefixLabel && !suffixLabel) {
    return inputElement;
  }

  return (
    <div className="relative flex w-full items-center">
      {prefixLabel ? (
        <div className="pointer-events-none absolute left-2 z-10 flex items-center">
          <span className="select-none text-xs font-medium text-muted-foreground">{prefixLabel}</span>
        </div>
      ) : null}
      {inputElement}
      {suffixLabel ? (
        <div className="pointer-events-none absolute right-2 z-10 flex items-center">
          <span className="select-none text-xs font-medium text-muted-foreground">{suffixLabel}</span>
        </div>
      ) : null}
    </div>
  );
}
// @genesys-end

Input.displayName = 'Input';

export { Input };
